import { Link, useParams } from "react-router-dom";
import { ExternalLink, ArrowRight } from "lucide-react";
import { PublicHeader, Footer } from "../components/PublicChrome";
import { DgcEmblem } from "../components/Logo";
import { HeroVideo } from "../components/HeroVideo";
import { Linkify } from "../components/heritage/Linkify";
import { PEOPLE, SOURCES, DIVISIONS } from "../lib/heritage";

const BRAND_NAMES = Object.fromEntries(DIVISIONS.flatMap((d) => d.items.map(([n, , , s]) => [s, n])));

export default function Person() {
  const { slug } = useParams();
  const p = PEOPLE[slug];
  if (!p) return <div className="p-10" data-testid="person-not-found"><PublicHeader />Bio not found.</div>;
  return (
    <div data-testid={`person-page-${slug}`}>
      <PublicHeader />
      <section className="bg-brand-deep text-white relative overflow-hidden">
        {p.video && <HeroVideo id={p.video[0]} title={p.name} credit={p.video[1]} creditUrl={`https://youtube.com/shorts/${p.video[0]}`} />}
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rise">
            <div className="eyebrow !text-[color:var(--gold-hi)] mb-4">Biography · {p.title}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] drop-shadow-[0_2px_12px_rgba(0,0,0,.35)]">{p.name}</h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-xl leading-relaxed"><Linkify text={p.summary} light /></p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#life" className="btn-gold !bg-[color:var(--gold-hi)] !text-[#064D29] !border-white/40 hover:!bg-white" data-testid="person-life-btn">Life & work</a>
              <Link to="/organisation" className="btn-ghost" data-testid="person-hero-org-btn">The Organisation</Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg border-t border-white/20 pt-6">
              {p.stats.map(([v, l]) => (
                <div key={l}><div className="font-display text-2xl md:text-3xl font-bold text-[color:var(--gold-hi)]">{v}</div><div className="font-data text-[9px] uppercase tracking-wider text-white/70 mt-1">{l}</div></div>
              ))}
            </div>
          </div>
          <div className="flex justify-center rise" style={{ animationDelay: ".15s" }}>
            <div className="relative">
              <div className="absolute -inset-6 rounded-full border border-[color:var(--gold-hi)]/50" />
              <DgcEmblem size={320} className="relative shadow-[0_30px_80px_-20px_rgba(0,0,0,.5)] hidden md:block" />
              <DgcEmblem size={220} className="relative md:hidden" />
            </div>
          </div>
        </div>
      </section>
      <section id="life" className="max-w-6xl mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="eyebrow mb-6">Life & work</div>
          <ol className="space-y-5 border-l-2 border-[color:var(--gold-hi)]/60 pl-6">
            {p.facts.map(([t, s], i) => (
              <li key={i} className="relative" data-testid={`person-fact-${i}`}>
                <span className="absolute -left-[32px] top-1.5 w-4 h-4 rounded-full bg-[#C9A227] ring-4 ring-white" />
                <p className="text-ink text-base leading-relaxed"><Linkify text={t} /></p>
                <a href={SOURCES[s][1]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-data text-[10px] uppercase tracking-wider text-brand-green hover:text-[color:var(--gold)]">Source: {SOURCES[s][0]} <ExternalLink size={10} /></a>
              </li>
            ))}
          </ol>
        </div>
        <div>
          {p.photo
            ? <img src={p.photo} alt={p.name} className="w-full rounded-md gold-frame mb-8 object-cover object-top max-h-96" data-testid="person-photo" />
            : <div className="w-full h-64 rounded-md bg-[#F4F8F5] border-2 border-dashed border-[color:var(--gold-hi)]/60 flex flex-col items-center justify-center gap-3 mb-8" data-testid="person-photo-placeholder"><DgcEmblem size={90} /><span className="font-data text-[10px] uppercase tracking-wider text-ink-3">Official photo: to be supplied</span></div>}
          <div className="eyebrow mb-4">Connected establishments</div>
          <div className="space-y-2">
            {p.brands.map((b) => (
              <Link key={b} to={`/entities/${b}`} className="panel panel-hover flex items-center justify-between px-4 py-3 text-sm text-ink" data-testid={`person-brand-${b}`}>
                {BRAND_NAMES[b]} <ArrowRight size={14} className="text-[color:var(--gold)]" />
              </Link>
            ))}
          </div>
          <Link to="/organisation" className="btn-ghost w-full mt-6" data-testid="person-org-link">View full organisation</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
